import { useState, useEffect } from "react";
import { useAuth } from "contexts/AuthContext";
import { Activity, CheckCircle, XCircle, AlertCircle, Loader2, RefreshCw, Clock, Play, CalendarClock } from "lucide-react";

const STATUS_CONFIG = {
  connected: { icon: CheckCircle, color: "#10b981", label: "Connected" },
  error: { icon: XCircle, color: "#ef4444", label: "Error" },
  not_configured: { icon: AlertCircle, color: "#f59e0b", label: "Not Configured" },
};

const SERVICE_LABELS = {
  mongodb: "MongoDB",
  firebase: "Firebase Auth",
  reddit_api: "Reddit API",
  openrouter: "OpenRouter (LLM)",
  keywords_everywhere: "Keywords Everywhere",
  adspower: "AdsPower",
};

function InfoCard({ label, value, color }) {
  return (
    <div style={{ background: "#111113", borderRadius: 8, padding: 14 }}>
      <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
      <div style={{ fontSize: 13, color: color || "#e5e5e5", fontWeight: 500 }}>{value}</div>
    </div>
  );
}

export default function HealthDashboard() {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [cronData, setCronData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cronLoading, setCronLoading] = useState(true);
  const [triggeringDaily, setTriggeringDaily] = useState(false);
  const [triggeringWeekly, setTriggeringWeekly] = useState(false);
  const [error, setError] = useState("");

  const fetchCronStatus = async () => {
    try {
      const res = await api.get("/cron/status");
      setCronData(res.data);
    } catch (err) {
      console.error("Failed to fetch cron status:", err);
    } finally {
      setCronLoading(false);
    }
  };

  useEffect(() => { fetchCronStatus(); }, []); // eslint-disable-line

  const runCheck = async () => {
    setLoading(true);
    setError("");
    try {
      const [healthRes, cronRes] = await Promise.all([
        api.get("/health-dashboard"),
        api.get("/cron/status"),
      ]);
      setData(healthRes.data);
      setCronData(cronRes.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to fetch health status");
    } finally {
      setLoading(false);
    }
  };

  const triggerDaily = async () => {
    setTriggeringDaily(true);
    try {
      const res = await api.post("/cron/trigger");
      alert(`Daily check triggered! Checking ${res.data.checking} entries (${res.data.total_unknown} unknown, ${res.data.total_live || 0} live)`);
      setTimeout(fetchCronStatus, 5000);
      setTimeout(fetchCronStatus, 15000);
    } catch (err) {
      alert(err.response?.data?.message || err.response?.data?.detail || "Trigger failed");
    } finally {
      setTriggeringDaily(false);
    }
  };

  const triggerWeekly = async () => {
    setTriggeringWeekly(true);
    try {
      const res = await api.post("/cron/trigger-weekly");
      alert(`Weekly stats re-fetch triggered! Re-fetching upvotes/rank for ${res.data.live_entries} live entries. This runs in the background.`);
      setTimeout(fetchCronStatus, 10000);
      setTimeout(fetchCronStatus, 30000);
    } catch (err) {
      alert(err.response?.data?.message || err.response?.data?.detail || "Trigger failed");
    } finally {
      setTriggeringWeekly(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Never";
    try { return new Date(dateStr).toLocaleString(); }
    catch { return dateStr; }
  };

  const resultColor = (result) => {
    if (!result) return "#6b7280";
    if (result.includes("success")) return "#10b981";
    return "#ef4444";
  };

  return (
    <div style={{ padding: "20px 0" }} data-testid="health-dashboard">
      {/* Daily Status Check */}
      <div style={{ background: "#18181b", border: "1px solid #27272a", borderRadius: 10, padding: 20, marginBottom: 20 }} data-testid="cron-status">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, color: "#e5e5e5", display: "flex", alignItems: "center", gap: 8 }}>
            <Clock size={18} /> Daily Status Check
          </h2>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-sm btn-secondary" onClick={fetchCronStatus} disabled={cronLoading} data-testid="refresh-cron-btn">
              <RefreshCw size={13} className={cronLoading ? "animate-spin" : ""} /> Refresh
            </button>
            <button className="btn btn-sm btn-primary" onClick={triggerDaily} disabled={triggeringDaily} data-testid="trigger-cron-btn">
              {triggeringDaily ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}
              {triggeringDaily ? " Running..." : " Run Now"}
            </button>
          </div>
        </div>
        <p style={{ fontSize: 12, color: "#6b7280", margin: "0 0 14px" }}>
          Checks status of unknown + live posts/comments on Reddit every day at 06:00 UTC.
        </p>
        {cronLoading && !cronData ? (
          <div style={{ textAlign: "center", padding: 16, color: "#6b7280" }}>
            <Loader2 size={16} className="animate-spin" style={{ margin: "0 auto 8px", display: "block" }} /> Loading...
          </div>
        ) : cronData ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 12 }}>
            <InfoCard label="Scheduler" value={cronData.scheduler_running ? "Active" : "Stopped"} color={cronData.scheduler_running ? "#10b981" : "#ef4444"} />
            <InfoCard label="Next Run" value={formatDate(cronData.daily?.next_run || cronData.next_run)} />
            <InfoCard label="Last Run" value={formatDate(cronData.daily?.last_run || cronData.last_run)} />
            <InfoCard label="Last Result" value={cronData.daily?.last_result || cronData.last_result || "No runs yet"} color={resultColor(cronData.daily?.last_result || cronData.last_result)} />
            <InfoCard label="Unknown Entries" value={cronData.unknown_count ?? "—"} color="#f59e0b" />
            <InfoCard label="Live Entries" value={cronData.live_count ?? "—"} color="#10b981" />
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: 16, color: "#6b7280" }}>Failed to load. Click Refresh.</div>
        )}
      </div>

      {/* Weekly Stats Re-fetch */}
      <div style={{ background: "#18181b", border: "1px solid #27272a", borderRadius: 10, padding: 20, marginBottom: 20 }} data-testid="weekly-refetch-status">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, color: "#e5e5e5", display: "flex", alignItems: "center", gap: 8 }}>
            <CalendarClock size={18} /> Weekly Stats Re-fetch
          </h2>
          <button className="btn btn-sm btn-primary" onClick={triggerWeekly} disabled={triggeringWeekly} data-testid="trigger-weekly-btn">
            {triggeringWeekly ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}
            {triggeringWeekly ? " Running..." : " Run Now"}
          </button>
        </div>
        <p style={{ fontSize: 12, color: "#6b7280", margin: "0 0 14px" }}>
          Re-fetches upvotes, rank, and comments for all live entries every Sunday at 04:00 UTC. Keeps stats fresh without manual intervention.
        </p>
        {cronData?.weekly ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 12 }}>
            <InfoCard label="Next Run" value={formatDate(cronData.weekly.next_run)} />
            <InfoCard label="Last Run" value={formatDate(cronData.weekly.last_run)} />
            <InfoCard label="Last Result" value={cronData.weekly.last_result || "No runs yet"} color={resultColor(cronData.weekly.last_result)} />
            <InfoCard label="Live Entries" value={cronData.live_count ?? "—"} color="#10b981" />
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 12 }}>
            <InfoCard label="Next Run" value="Sunday 04:00 UTC" />
            <InfoCard label="Last Run" value="Never" />
            <InfoCard label="Last Result" value="No runs yet" color="#6b7280" />
            <InfoCard label="Live Entries" value={cronData?.live_count ?? "—"} color="#10b981" />
          </div>
        )}
      </div>

      {/* Integration Health */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600, color: "#e5e5e5", display: "flex", alignItems: "center", gap: 8 }}>
          <Activity size={18} /> Integration Health
        </h2>
        <button className="btn btn-primary btn-sm" onClick={runCheck} disabled={loading} data-testid="run-health-check">
          {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
          {loading ? " Checking..." : " Run Health Check"}
        </button>
      </div>

      {error && <div className="login-error">{error}</div>}

      {!data && !loading && !error && (
        <div style={{ textAlign: "center", padding: 32, color: "#6b7280" }}>
          Click "Run Health Check" to test all integrations
        </div>
      )}

      {data && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 12 }}>
          {Object.entries(data).map(([key, val]) => {
            const cfg = STATUS_CONFIG[val.status] || STATUS_CONFIG.error;
            const Icon = cfg.icon;
            return (
              <div key={key} data-testid={`health-${key}`} style={{
                background: "#18181b", border: `1px solid ${cfg.color}33`, borderRadius: 10,
                padding: 16, display: "flex", flexDirection: "column", gap: 6,
              }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "#e5e5e5" }}>{SERVICE_LABELS[key] || key}</span>
                  <Icon size={16} color={cfg.color} />
                </div>
                <div style={{ fontSize: 12, color: cfg.color, fontWeight: 500 }}>{cfg.label}</div>
                <div style={{ fontSize: 11, color: "#9ca3af", lineHeight: 1.4 }}>{val.message}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
